/**
 * Teams: the client for `/api/teams`, and the tab's half of the one room in
 * this product with a door on it.
 *
 * The design and its non-goals are [docs/teams.md](../../../docs/teams.md).
 * What this file is responsible for is small and worth stating: it holds the
 * roster and the invitations, it asks the Worker to change them, and it never
 * decides anything. Every rule — who may invite, how many a team holds, how
 * many teams one learner may be in — is enforced in `worker/teams.ts`, and the
 * copies of `LIMITS.team` used here are for saying no *sooner*, never instead.
 *
 * ## It is the inbox's client, with the inbox's rules
 *
 * Nothing here may break the chat. Every route is on a Worker that may not be
 * deployed, behind a session that may have expired, and none of it is
 * load-bearing for a message arriving in Room 1. Every failure is an empty
 * roster rather than a thrown error, and a tab talking to a Worker that has
 * never heard of `/api/teams` behaves exactly like a learner who is in no
 * teams — which, on the day this shipped, was everybody.
 *
 * ## Why the room id comes from the server
 *
 * `Team.room` arrives on the wire rather than being built here out of
 * `team-` + the id. It is one string either way, and the reason is that a room
 * id is the protocol's to construct (`teamRoom`, packages/chat) and a tab that
 * assembles one by concatenation is a tab that will still be assembling one
 * the day the format changes. The Worker builds it with the same function the
 * door parses it with.
 */
import { LIMITS, cleanTeamName, parseRoomId, type RoomId } from '@engineer-os/chat';
import { chatUrl } from './chat';

/** One person on a roster. `tag` is null for somebody who has never opened a chat socket. */
export interface TeamMember {
  tag: string | null;
  name: string;
  avatarSeed: string | null;
  role: 'lead' | 'member';
  joinedAt: number;
}

/** One team this learner is in. */
export interface Team {
  id: string;
  name: string;
  room: RoomId;
  createdAt: number;
  /** This learner's own role, which is what the panel gates its buttons on. */
  role: 'lead' | 'member';
  members: TeamMember[];
}

/** An invitation waiting for an answer. */
export interface TeamInvite {
  id: number;
  teamId: string;
  teamName: string;
  fromName: string;
  fromTag: string | null;
  createdAt: number;
  /** How many are in it already, so the card can say "join 4 people". */
  size: number;
}

export interface TeamState {
  teams: Team[];
  invites: TeamInvite[];
}

export const NO_TEAMS: TeamState = { teams: [], invites: [] };

const SESSION: RequestInit = { credentials: 'include', cache: 'no-store' };

/* ── Reading ────────────────────────────────────────────────────────────── */

/**
 * Everything this account is in and everything it has been asked to join.
 *
 * Signed out is an empty state and not an error: the routes answer 404 without
 * a session on purpose, so there is nothing here that can tell a signed-out
 * learner from a Worker that is not there, and nothing that needs to.
 */
export async function fetchTeams(): Promise<TeamState> {
  try {
    const r = await fetch(chatUrl('/api/teams').href, SESSION);
    if (!r.ok) return NO_TEAMS;
    return readTeams(await r.json());
  } catch {
    return NO_TEAMS;
  }
}

/* ── The store ──────────────────────────────────────────────────────────────
   Until now this state lived inside `useTeams`, and the hook said why: there
   was exactly one reader, the chat panel, so a hook was the smaller thing and
   the file promised to become a store the day something outside the dock
   needed it. The learner's own profile screen is that day — it lists the teams
   somebody is in, and it is a screen the dock is mounted *over*, so two
   independent copies of this state would mean leaving a team in the panel and
   watching the page behind it go on showing it.

   One state, one request, and two readers that cannot disagree. */

export interface TeamsView extends TeamState {
  /** True while the first answer for this account is still out. */
  loading: boolean;
}

/**
 * The current value, as one frozen object.
 *
 * Frozen and replaced rather than edited, because this is the snapshot half of
 * `useSyncExternalStore` and React compares it by identity: a snapshot
 * assembled fresh on every call — `{...state, loading}` — is a different object
 * every time and re-renders forever.
 */
let view: TeamsView = { teams: [], invites: [], loading: false };

const listeners = new Set<() => void>();

/**
 * Whether the last thing this store was told about the account was that there
 * was one. `null` before anybody has said.
 *
 * This is what makes a second reader mounting free. A change of account has to
 * empty the store — a panel showing the previous account's teams is the one
 * failure here that looks like a leak — but a profile screen opening next to
 * an open panel is not a change of account, and treating it as one would blank
 * a list somebody was reading.
 */
let account: boolean | null = null;

/** In flight, so two readers asking at once is one request. */
let asking: Promise<void> | null = null;

/**
 * Bumped whenever the account changes. An answer that arrives for a
 * generation that is no longer current is dropped, which is the `live` ref the
 * hook used to hold, moved to where the request is.
 */
let generation = 0;

function publish(next: TeamsView): void {
  view = next;
  for (const listener of listeners) listener();
}

/** The snapshot half of `useSyncExternalStore`. */
export function teamsSnapshot(): TeamsView {
  return view;
}

/** The subscribe half. Returns the unsubscribe, as React asks. */
export function onTeams(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Ask the Worker again.
 *
 * Coalesced: a profile screen and a chat panel that mount in the same frame
 * make one request between them, and so do a panel opening and a write
 * finishing. There is no timer anywhere — an invitation is answered in hours,
 * and a tab polling a D1-backed route all day to find out whether somebody has
 * been asked to join a study group is a request every few seconds spent on
 * nothing. The moments worth spending one on are somebody looking and a write
 * landing, and those are the only two callers.
 */
export function refreshTeams(): Promise<void> {
  if (asking) return asking;
  const mine = generation;
  publish({ ...view, loading: true });
  asking = fetchTeams().then((next) => {
    asking = null;
    if (mine !== generation) return;
    publish({ ...next, loading: false });
  });
  return asking;
}

/**
 * Tell the store who is signed in, and take a fresh look if that is new.
 *
 * Every reader calls this on mount and on a change of account, and the guard
 * above is what makes those two different things: a change empties the store
 * first, so no reader ever draws one account's teams under another's name,
 * and a mount that changes nothing simply asks again.
 */
export function teamsForAccount(signedIn: boolean): void {
  if (account !== signedIn) {
    account = signedIn;
    generation++;
    asking = null;
    publish({ teams: [], invites: [], loading: false });
  }
  if (signedIn) void refreshTeams();
}

/* ── Writing ────────────────────────────────────────────────────────────── */

/**
 * What a write says back.
 *
 * `ok` and, when it is false, the Worker's own reason — which is a word from a
 * closed set (`too many teams`, `team is full`, `already a member`, `already
 * asked`, `not a lead`), so the panel can say something specific rather than
 * "something went wrong". An unrecognised reason renders as the general
 * failure, which is what a tab one release behind a Worker should do.
 */
export interface Wrote {
  ok: boolean;
  reason?: string;
  /** On a create or an accept: the room now worth opening. */
  room?: RoomId | null;
}

async function write(path: string, body?: unknown): Promise<Wrote> {
  try {
    const r = await fetch(chatUrl(path).href, {
      ...SESSION,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body ?? {}),
    });
    const answer = (await r.json().catch(() => null)) as { error?: unknown; room?: unknown } | null;
    if (!r.ok) return { ok: false, reason: typeof answer?.error === 'string' ? answer.error : undefined };
    return { ok: true, room: parseRoomId(answer?.room) };
  } catch {
    return { ok: false };
  }
}

/** Make one. The name is checked here too, so a hopeless one costs no request. */
export async function createTeam(name: string): Promise<Wrote> {
  if (!cleanTeamName(name)) return { ok: false, reason: 'bad name' };
  return write('/api/teams', { name });
}

/**
 * Ask somebody to join, by the tag the room shows for them.
 *
 * By tag because that is what the person doing the inviting is looking at —
 * they clicked a name in a room and the card under it is keyed by that tag —
 * and because the account id behind it never leaves the Worker.
 */
export async function inviteToTeam(teamId: string, tag: string): Promise<Wrote> {
  return write(`/api/teams/${teamId}/invite`, { tag });
}

export async function answerInvite(inviteId: number, accept: boolean): Promise<Wrote> {
  return write(`/api/teams/invites/${inviteId}/${accept ? 'accept' : 'decline'}`);
}

export async function leaveTeam(teamId: string): Promise<Wrote> {
  return write(`/api/teams/${teamId}/leave`);
}

/* ── Reading what the Worker said ───────────────────────────────────────── */

/**
 * Held to the same suspicion a stored blob is, and for the same reason: this
 * arrived over a network from a Worker that may be a deploy ahead or behind
 * this bundle. A team that will not parse is dropped and the rest survive — a
 * roster is a list, and one bad row in a list is not a reason to show none of
 * it.
 *
 * Exported for the tests, which is where every decision in this file is
 * actually checked: the fetch around it cannot run under a plain Node test
 * runner, because `chatUrl` reads `import.meta.env`.
 */
export function readTeams(body: unknown): TeamState {
  if (typeof body !== 'object' || body === null) return NO_TEAMS;
  const b = body as { teams?: unknown; invites?: unknown };
  return {
    teams: list(b.teams).map(team).filter((t): t is Team => t !== null).slice(0, LIMITS.team.perLearner),
    invites: list(b.invites).map(invite).filter((i): i is TeamInvite => i !== null).slice(0, LIMITS.team.invites),
  };
}

const list = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const text = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v : null);
const whole = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.trunc(v) : 0);
const seat = (v: unknown): 'lead' | 'member' => (v === 'lead' ? 'lead' : 'member');

function team(raw: unknown): Team | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const t = raw as Record<string, unknown>;
  const id = text(t.id);
  const name = text(t.name);
  // Through the protocol's own parser, never by concatenation: a room id this
  // tab cannot parse is one it must not try to open a socket to.
  const room = parseRoomId(t.room);
  if (!id || !name || !room) return null;
  return {
    id,
    name,
    room,
    createdAt: whole(t.createdAt),
    role: seat(t.role),
    // Capped at the limit the server enforces rather than trusted to be
    // within it, because the one thing a roster must not become is a list
    // long enough to matter in a 280px panel.
    members: list(t.members).map(member).filter((m): m is TeamMember => m !== null).slice(0, LIMITS.team.size),
  };
}

function member(raw: unknown): TeamMember | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const m = raw as Record<string, unknown>;
  const name = text(m.name);
  if (!name) return null;
  return {
    tag: typeof m.tag === 'string' && /^[0-9a-f]{4}$/.test(m.tag) ? m.tag : null,
    name,
    avatarSeed: text(m.avatarSeed),
    role: seat(m.role),
    joinedAt: whole(m.joinedAt),
  };
}

function invite(raw: unknown): TeamInvite | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const i = raw as Record<string, unknown>;
  const id = whole(i.id);
  const teamId = text(i.teamId);
  const teamName = text(i.teamName);
  const fromName = text(i.fromName);
  // An invitation with no id cannot be answered, and one with no team name or
  // no asker is a question with nothing in it. All four or none.
  if (!id || !teamId || !teamName || !fromName) return null;
  return {
    id,
    teamId,
    teamName,
    fromName,
    fromTag: typeof i.fromTag === 'string' && /^[0-9a-f]{4}$/.test(i.fromTag) ? i.fromTag : null,
    createdAt: whole(i.createdAt),
    size: whole(i.size),
  };
}

/* ── Asking about a room ────────────────────────────────────────────────── */

/**
 * The team whose room this is, out of a roster already in hand.
 *
 * This is how a team room gets its real name on screen. `roomLabel` in the
 * protocol answers the generic word for a team room, deliberately — a team is
 * called what its members called it, which lives in D1 and is not something a
 * pure function can know — so every place that draws a room name asks this
 * first and falls back to the protocol's answer.
 */
export function teamForRoom(teams: readonly Team[], room: RoomId): Team | null {
  return teams.find((t) => t.room === room) ?? null;
}
